/**
 * SRS / repaso espaciado (estilo Duolingo) — planificador PURO y DERIVADO.
 *
 * No introduce estado nuevo: el calendario de repaso se DERIVA de los campos ya
 * sincronizados de `lesson_progress` (`attempts`, `score`, `completed_at`,
 * `updated_at`). Repasar una lección ES volver a jugarla con el player normal —
 * el write-path existente incrementa `attempts`, re-corrige `score` en el
 * servidor y refresca `updated_at`, con lo que el calendario avanza solo. Cero
 * migraciones, cero write-path nuevo, offline-first gratis.
 *
 * Escalera de intervalos (SM-2 lite): 1 → 3 → 7 → 14 → 30 → 60 días según el
 * número de pasadas (`attempts`), moderada por la calidad de la última nota:
 *   - score ≥ STRONG_SCORE  → intervalo completo de la escalera.
 *   - score < STRONG_SCORE  → medio intervalo (mínimo 1 día): lo flojo vuelve antes.
 * Solo entran lecciones COMPLETADAS (una lección fallada sigue siendo el nodo
 * "actual" de la senda, no un repaso).
 */

/** Escalera de intervalos en días por número de pasadas (attempts 1..N). */
export const SRS_INTERVAL_LADDER_DAYS = [1, 3, 7, 14, 30, 60] as const;

/** Nota a partir de la cual la última pasada se considera sólida. */
export const SRS_STRONG_SCORE = 0.85;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Orden determinista: por vencimiento asc, desempate estable por lessonId. */
function byDueThenId(a: ReviewItem, b: ReviewItem): number {
  if (a.dueAt !== b.dueAt) {
    return a.dueAt - b.dueAt;
  }
  if (a.lessonId < b.lessonId) {
    return -1;
  }
  return a.lessonId > b.lessonId ? 1 : 0;
}

/** Fila mínima de progreso que necesita el planificador (subset de lesson_progress). */
export interface SrsProgressRow {
  lesson_id: string | null;
  course_id: string | null;
  status: string | null;
  score: number | null;
  attempts: number | null;
  completed_at: string | null;
  updated_at: string | null;
}

export interface ReviewItem {
  lessonId: string;
  courseId: string;
  /** Epoch ms en que toca el repaso. */
  dueAt: number;
  /** Días del intervalo aplicado (para la UI). */
  intervalDays: number;
  /** true si ya venció (dueAt <= now). */
  due: boolean;
}

export interface ReviewQueue {
  /** Vencidos, los más atrasados primero. */
  due: ReviewItem[];
  /** Por venir, el más próximo primero. */
  upcoming: ReviewItem[];
}

/**
 * Intervalo (días) hasta el próximo repaso según pasadas y calidad de la última
 * nota. `attempts` cuenta pasadas completadas (≥1); valores raros se acotan.
 */
export function computeReviewIntervalDays(attempts: number, score: number): number {
  const idx = Math.min(Math.max(Math.floor(attempts), 1), SRS_INTERVAL_LADDER_DAYS.length) - 1;
  const ladder = SRS_INTERVAL_LADDER_DAYS[idx] ?? 1;
  if (score >= SRS_STRONG_SCORE) {
    return ladder;
  }
  // Pasada floja: vuelve antes (medio intervalo, nunca menos de 1 día).
  return Math.max(1, Math.round(ladder / 2));
}

/**
 * Momento del próximo repaso de una fila de progreso, o `null` si la fila no
 * entra en el ciclo de repaso (no completada o sin fechas utilizables).
 * La base es el ÚLTIMO toque (`updated_at`, con `completed_at` de respaldo):
 * cada repaso jugado empuja el siguiente.
 */
export function computeNextReviewAt(row: SrsProgressRow): ReviewItem | null {
  if (row.status !== "completed" || !row.lesson_id || !row.course_id) {
    return null;
  }
  const baseIso = row.updated_at ?? row.completed_at;
  if (!baseIso) {
    return null;
  }
  const base = Date.parse(baseIso);
  if (Number.isNaN(base)) {
    return null;
  }
  const attempts = row.attempts ?? 1;
  const score = row.score ?? 0;
  const intervalDays = computeReviewIntervalDays(attempts, score);
  return {
    lessonId: row.lesson_id,
    courseId: row.course_id,
    dueAt: base + intervalDays * DAY_MS,
    intervalDays,
    due: false,
  };
}

/**
 * Cola de repaso derivada del progreso sincronizado: separa vencidos de
 * próximos y ordena determinista (vencidos: más atrasado primero; próximos:
 * más cercano primero; empates por lessonId para estabilidad).
 */
export function buildReviewQueue(rows: readonly SrsProgressRow[], now: number): ReviewQueue {
  const items: ReviewItem[] = [];
  for (const row of rows) {
    const item = computeNextReviewAt(row);
    if (item) {
      items.push({ ...item, due: item.dueAt <= now });
    }
  }
  return {
    due: items.filter((i) => i.due).toSorted(byDueThenId),
    upcoming: items.filter((i) => !i.due).toSorted(byDueThenId),
  };
}
